import { X, Shield, AlertTriangle } from "lucide-react";
import { Customer } from "../../types/customer";

interface UnbanConfirmationModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (customerId: string) => void;
  isLoading?: boolean;
}

export const UnbanConfirmationModal: React.FC<UnbanConfirmationModalProps> = ({
  customer,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  if (!customer || !isOpen) return null;

  const getBanStatusInfo = () => {
    switch (customer.banStatus) {
      case "under_monitoring":
        return {
          title: "Remove from Monitoring",
          description: "This customer will no longer be under monitoring.",
          icon: <Shield className="w-5 h-5 text-green-400" />,
          color: "green",
        };
      case "temporary_ban":
        return {
          title: "Remove Temporary Ban",
          description:
            "This customer's temporary ban will be lifted immediately.",
          icon: <Shield className="w-5 h-5 text-green-400" />,
          color: "green",
        };
      case "permanently_banned":
        return {
          title: "Remove Permanent Ban",
          description:
            "This customer's permanent ban will be lifted. They will regain full access.",
          icon: <AlertTriangle className="w-5 h-5 text-orange-400" />,
          color: "orange",
        };
      default:
        return {
          title: "Unban Customer",
          description: "This customer's ban status will be removed.",
          icon: <Shield className="w-5 h-5 text-green-400" />,
          color: "green",
        };
    }
  };

  const statusInfo = getBanStatusInfo();

  const handleConfirm = () => {
    onConfirm(customer.id);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              {statusInfo.icon}
              <h2 className="text-lg font-semibold text-white">
                {statusInfo.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              disabled={isLoading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Customer Info */}
            <div className="mb-4 p-3 bg-slate-700 rounded-lg">
              <div className="text-sm text-slate-400">Customer</div>
              <div className="text-white font-medium">{customer.name}</div>
              <div className="text-sm text-slate-300">{customer.email}</div>
              <div className="text-xs text-slate-400 mt-1">
                Player ID: {customer.playerId}
              </div>
            </div>

            {/* Current Status */}
            <div className="mb-4 p-3 bg-slate-700 rounded-lg">
              <div className="text-sm text-slate-400 mb-1">Current Status</div>
              <div className="text-white capitalize">
                {customer.banStatus.replace(/_/g, " ")}
              </div>
              {customer.banEndDate && (
                <div className="text-xs text-slate-400 mt-1">
                  Ban ends: {new Date(customer.banEndDate).toLocaleDateString()}
                </div>
              )}
              {customer.banRemarks && (
                <div className="text-xs text-slate-300 mt-1">
                  Reason: {customer.banRemarks}
                </div>
              )}
            </div>

            {/* Warning/Info */}
            <div
              className={`mb-6 p-3 rounded-lg ${
                statusInfo.color === "orange"
                  ? "bg-orange-900/20 border border-orange-600"
                  : "bg-green-900/20 border border-green-600"
              }`}
            >
              <p
                className={`text-sm ${
                  statusInfo.color === "orange"
                    ? "text-orange-200"
                    : "text-green-200"
                }`}
              >
                {statusInfo.description}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  statusInfo.color === "orange"
                    ? "bg-orange-600 hover:bg-orange-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isLoading ? "Processing..." : "Confirm Unban"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
